import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useProjects } from "@/context/ProjectContext";
import { useAuth, isWriter, isAdmin } from "@/context/AuthContext";
import { PageHeader, Section, SourceLink, EmptyState } from "@/components/primitives";
import { Modal, Field, inputCls, PrimaryButton, GhostButton } from "@/components/Modal";
import { StatusBadge } from "@/components/StatusBadge";
import { AudienceSelector } from "@/components/AudienceSelector";
import { toast } from "sonner";
import { ClockCounterClockwise, Trash, Plus } from "@phosphor-icons/react";

const STATUSES = ["Open", "In progress", "Resolved", "Not verified"];

export default function Closeout() {
  const { currentId } = useProjects();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [historyFor, setHistoryFor] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newItem, setNewItem] = useState({ title: "", due_date: "2026-11-01" });

  const load = useCallback(async () => {
    if (!currentId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/api/projects/${currentId}/closeout`);
      setItems(data);
    } finally {
      setLoading(false);
    }
  }, [currentId]);

  useEffect(() => { load(); }, [load]);

  const openEdit = (r) => {
    setForm({
      status: r.status, owner: r.owner || "", due_date: r.due_date || "",
      notes: r.notes || "", evidence: (r.evidence_links || []).join("\n"),
      audiences: r.audiences || ["internal"],
    });
    setEditing(r);
  };

  const save = async () => {
    try {
      await api.put(`/api/closeout/${editing.id}`, {
        status: form.status,
        owner: form.owner || null,
        due_date: form.due_date || null,
        notes: form.notes,
        evidence_links: form.evidence.split("\n").map((s) => s.trim()).filter(Boolean),
        audiences: form.audiences,
      });
      toast.success("Request updated");
      setEditing(null);
      load();
    } catch { toast.error("Update failed"); }
  };

  const create = async () => {
    if (!newItem.title.trim()) { toast.error("Title required"); return; }
    try {
      await api.post(`/api/projects/${currentId}/closeout`, newItem);
      toast.success("Request added");
      setCreating(false);
      setNewItem({ title: "", due_date: "2026-11-01" });
      load();
    } catch { toast.error("Create failed"); }
  };

  const remove = async (r) => {
    if (!window.confirm("Delete this request?")) return;
    try { await api.delete(`/api/closeout/${r.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  if (!currentId || loading) return <div className="cm-label animate-pulse">Loading…</div>;

  const openCount = items.filter((i) => i.status === "Open").length;

  return (
    <div data-testid="closeout-page">
      <PageHeader
        eyebrow="Closeout"
        title="Closeout Review"
        description="Open requests from the Sept 30, 2026 review. Requests are not marked complete from folder existence alone — each needs confirmed evidence."
      >
        {isWriter(user) && <PrimaryButton data-testid="closeout-add-btn" onClick={() => setCreating(true)}><span className="inline-flex items-center gap-1"><Plus size={14} /> New request</span></PrimaryButton>}
      </PageHeader>

      <div className="mb-6 border border-[rgba(245,158,11,0.3)] bg-[rgba(245,158,11,0.08)] px-4 py-3 text-sm" data-testid="closeout-unresolved-overview">
        <span className="cm-label text-[#F59E0B]">Unresolved overview · </span>
        <span className="text-[#F9F9F8]">{openCount} of {items.length} requests still OPEN · requested due Nov 1, 2026 · owners unassigned unless stated.</span>
      </div>

      {items.length === 0 ? (
        <EmptyState title="No closeout requests" />
      ) : (
        <div className="overflow-x-auto border border-[#2C2C2A]">
          <table className="w-full min-w-[800px] text-sm">
            <thead>
              <tr className="border-b border-[#2C2C2A] text-left">
                <th className="cm-label p-3 w-8">#</th>
                <th className="cm-label p-3">Request</th>
                <th className="cm-label p-3">Status</th>
                <th className="cm-label p-3">Owner</th>
                <th className="cm-label p-3">Due</th>
                <th className="cm-label p-3">Evidence</th>
                <th className="cm-label p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-b border-[#2C2C2A] align-top last:border-0 hover:bg-[#161614]" data-testid={`closeout-row-${r.index}`}>
                  <td className="p-3 text-[#A1A19D]">{r.index}</td>
                  <td className="p-3 text-[#F9F9F8] max-w-md">
                    {r.title}
                    {r.notes && <div className="mt-1 text-xs text-[#A1A19D]">{r.notes}</div>}
                  </td>
                  <td className="p-3"><StatusBadge status={r.status} data-testid={`closeout-status-${r.index}`} /></td>
                  <td className="p-3 text-[#A1A19D]">{r.owner || "Unassigned"}</td>
                  <td className="p-3 text-[#A1A19D]">{r.due_date}</td>
                  <td className="p-3">
                    {r.evidence_links?.length
                      ? r.evidence_links.map((l, i) => <div key={i}><SourceLink url={l} label={`Link ${i + 1}`} /></div>)
                      : <span className="text-xs italic text-[#6b6b66]">None</span>}
                  </td>
                  <td className="p-3">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => setHistoryFor(r)} title="History" className="text-[#A1A19D] hover:text-[#F9F9F8]" data-testid={`closeout-history-${r.index}`}><ClockCounterClockwise size={16} /></button>
                      {isWriter(user) && <button onClick={() => openEdit(r)} className="text-[#F59E0B] hover:underline text-xs" data-testid={`closeout-edit-${r.index}`}>Edit</button>}
                      {isAdmin(user) && <button onClick={() => remove(r)} title="Delete" className="text-[#A1A19D] hover:text-[#e07a7a]"><Trash size={16} /></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={!!editing} onClose={() => setEditing(null)} title={`Edit request #${editing?.index}`} testid="closeout-edit-modal">
        <p className="mb-4 text-xs text-[#A1A19D]">{editing?.title}</p>
        <Field label="Status">
          <select className={inputCls} data-testid="closeout-status-select" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Owner"><input className={inputCls} data-testid="closeout-owner" value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })} placeholder="Unassigned" /></Field>
        <Field label="Due date"><input className={inputCls} type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></Field>
        <Field label="Notes"><textarea className={inputCls} rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
        <Field label="Evidence links (one per line)"><textarea className={inputCls} rows={2} data-testid="closeout-evidence" value={form.evidence} onChange={(e) => setForm({ ...form, evidence: e.target.value })} /></Field>
        <div className="mb-4"><AudienceSelector value={form.audiences} onChange={(a) => setForm({ ...form, audiences: a })} /></div>
        <div className="flex justify-end gap-2">
          <GhostButton onClick={() => setEditing(null)}>Cancel</GhostButton>
          <PrimaryButton data-testid="closeout-save" onClick={save}>Save</PrimaryButton>
        </div>
      </Modal>

      <Modal open={!!historyFor} onClose={() => setHistoryFor(null)} title={`Change history · #${historyFor?.index}`} testid="closeout-history-modal">
        <ul className="space-y-3">
          {(historyFor?.history || []).map((h, i) => (
            <li key={i} className="border-l-2 border-[#F59E0B] pl-3">
              <div className="cm-label">{new Date(h.at).toLocaleString()} · {h.actor}</div>
              <div className="text-sm text-[#F9F9F8]">{h.change}</div>
            </li>
          ))}
          {!historyFor?.history?.length && <li className="text-sm text-[#A1A19D]">No history.</li>}
        </ul>
      </Modal>

      <Modal open={creating} onClose={() => setCreating(false)} title="New closeout request" testid="closeout-create-modal">
        <Field label="Request"><textarea className={inputCls} rows={3} data-testid="closeout-new-title" value={newItem.title} onChange={(e) => setNewItem({ ...newItem, title: e.target.value })} /></Field>
        <Field label="Due date"><input className={inputCls} type="date" value={newItem.due_date} onChange={(e) => setNewItem({ ...newItem, due_date: e.target.value })} /></Field>
        <div className="flex justify-end gap-2">
          <GhostButton onClick={() => setCreating(false)}>Cancel</GhostButton>
          <PrimaryButton data-testid="closeout-create-save" onClick={create}>Add request</PrimaryButton>
        </div>
      </Modal>
    </div>
  );
}
